import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useRef,
} from 'react';

interface ExpireInfo {
  key: ExpireKey;
  expireTime: Date | null;
  isExpired: boolean;
  remainingTime: number | null;
}

export const EXPIRE_PATTERNS = {
  TRAIN_EUROPE_BEFORE_BOOKING: 'train_europe_before_booking',
  TRAIN_EUROPE_BOOKING: 'train_europe_booking',
} as const;

type ExpirePattern = (typeof EXPIRE_PATTERNS)[keyof typeof EXPIRE_PATTERNS];
type ExpireKey = `${ExpirePattern}_${string}`;

interface ExpireContextType {
  isExpired: (key: ExpireKey) => boolean;
  setExpireTime: (key: ExpireKey, expireTimeString: string | undefined) => void;
  getRemainingTime: (key: ExpireKey) => number | null;
  clearExpireInfo: (key: ExpireKey) => void;
  getPatternKey: (path: string) => ExpireKey | null;
}

interface PathInfo {
  pattern: ExpirePattern | null;
  id: string;
}

const ExpireContext = createContext<ExpireContextType | undefined>(undefined);

const parsePathInfo = (path: string): PathInfo => {
  const defaultResult = { pattern: null, id: 'default' };

  if (path.includes('/search/train/europe/') || path.includes('/ticket/train/europe/')) {
    const matches = path.match(/\/(search|ticket)\/train\/europe\/([^/]+)/);
    if (matches && matches[2]) {
      return {
        pattern: EXPIRE_PATTERNS.TRAIN_EUROPE_BEFORE_BOOKING,
        id: matches[2],
      };
    }
  }

  if (path.includes('/traveler/train/europe/')) {
    const matches = path.match(/\/traveler\/train\/europe\/([^/]+)/);
    if (matches && matches[1]) {
      return {
        pattern: EXPIRE_PATTERNS.TRAIN_EUROPE_BOOKING,
        id: matches[1],
      };
    }
  }

  return defaultResult;
};

interface ExpireProviderProps {
  children: ReactNode;
}

const ExpireProvider = ({ children }: ExpireProviderProps) => {
  const [expireInfo, setExpireInfo] = useState<ExpireInfo | null>(null);
  const timerIdRef = useRef<number | null>(null);

  const getPatternKey = useCallback((path: string): ExpireKey | null => {
    const { pattern, id } = parsePathInfo(path);
    if (!pattern) return null;

    return `${pattern}_${id}` as ExpireKey;
  }, []);

  const scheduleExpiration = useCallback((key: ExpireKey, expireTime: Date) => {
    if (timerIdRef.current !== null) {
      clearTimeout(timerIdRef.current);
      timerIdRef.current = null;
    }

    const now = new Date();
    const timeDiff = expireTime.getTime() - now.getTime();

    if (timeDiff <= 0) {
      setExpireInfo({
        key,
        expireTime,
        isExpired: true,
        remainingTime: 0,
      });
      return;
    }

    const remainingSecs = Math.ceil(timeDiff / 1000);

    const nextUpdateTime = timeDiff % 1000 || 1000;

    timerIdRef.current = window.setTimeout(() => {
      const updatedRemainingTime = remainingSecs - 1;
      const isNowExpired = updatedRemainingTime <= 0;

      setExpireInfo({
        key,
        expireTime,
        isExpired: isNowExpired,
        remainingTime: isNowExpired ? 0 : updatedRemainingTime,
      });

      if (!isNowExpired) {
        scheduleExpiration(key, expireTime);
      }
    }, nextUpdateTime);
  }, []);

  const setExpireTime = useCallback(
    (key: ExpireKey, expireTimeString: string | undefined) => {
      if (!expireTimeString) {
        return;
      }

      const formattedTime = expireTimeString.split('.')[0];
      const newExpireTime = new Date(formattedTime);

      if (isNaN(newExpireTime.getTime())) {
        console.warn('Invalid expire time format:', expireTimeString);
        return;
      }

      setExpireInfo({
        key,
        expireTime: newExpireTime,
        isExpired: false,
        remainingTime: null,
      });

      scheduleExpiration(key, newExpireTime);
    },
    [scheduleExpiration],
  );

  const isExpired = useCallback(
    (key: ExpireKey) => {
      return expireInfo?.key === key ? expireInfo.isExpired : false;
    },
    [expireInfo],
  );

  const getRemainingTime = useCallback(
    (key: ExpireKey) => {
      return expireInfo?.key === key ? expireInfo.remainingTime : null;
    },
    [expireInfo],
  );

  const clearExpireInfo = useCallback(
    (key: ExpireKey) => {
      if (expireInfo?.key === key) {
        if (timerIdRef.current !== null) {
          clearTimeout(timerIdRef.current);
          timerIdRef.current = null;
        }

        setExpireInfo(null);
      }
    },
    [expireInfo],
  );

  useEffect(() => {
    return () => {
      if (timerIdRef.current !== null) {
        clearTimeout(timerIdRef.current);
        timerIdRef.current = null;
      }
    };
  }, []);

  const contextValue = useMemo(
    () => ({
      isExpired,
      setExpireTime,
      getRemainingTime,
      clearExpireInfo,
      getPatternKey,
    }),
    [isExpired, setExpireTime, getRemainingTime, clearExpireInfo, getPatternKey],
  );

  return <ExpireContext.Provider value={contextValue}>{children}</ExpireContext.Provider>;
};

export const useExpire = () => {
  const context = useContext(ExpireContext);
  if (context === undefined) {
    throw new Error('useExpire must be used within an ExpireProvider');
  }
  return context;
};

export default ExpireProvider;
